import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  breadcrumbs?: Array<{ name: string; url: string }>;
  articleData?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
  };
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = 'Hundesport, Berlin, Adlershof, Agility, Unterordnung, Rally Obedience, Turnierhundesport, DVG, Hundetraining, Hundeverein, Hundesportverein',
  image = '/images/common/og-image.jpg',
  url = 'https://www.hsv-adlershof.berlin',
  type = 'website',
  breadcrumbs,
  articleData
}) => {
  const fullTitle = `${title} | HSV Berlin-Adlershof e.V.`;
  const siteUrl = 'https://www.hsv-adlershof.berlin';
  const fullUrl = url.startsWith('http') ? url : `${siteUrl}${url}`;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  // Organization Structured Data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    "name": "HSV Berlin-Adlershof e.V.",
    "alternateName": "Hundesportverein Berlin-Adlershof",
    "url": "https://www.hsv-adlershof.berlin",
    "logo": "https://www.hsv-adlershof.berlin/images/common/HSV_Logo.jpg",
    "image": "https://www.hsv-adlershof.berlin/images/common/og-image.jpg",
    "description": "Hundesportverein in Berlin-Adlershof seit 1948. Training für Unterordnung, Agility, Rally Obedience und Turnierhundesport.",
    "foundingDate": "1948",
    "telephone": "+49-162-2068729",
    "email": "hsv.berlin.adlershof@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Birkenweg",
      "addressLocality": "Berlin",
      "addressRegion": "Berlin",
      "postalCode": "12439",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 52.445896,
      "longitude": 13.542682
    },
    "memberOf": {
      "@type": "Organization",
      "name": "DVG - Deutscher Verband der Gebrauchshundesportvereine e.V."
    }
  };

  // Local Business Structured Data
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "HSV Berlin-Adlershof e.V.",
    "image": "https://www.hsv-adlershof.berlin/images/common/og-image.jpg",
    "logo": "https://www.hsv-adlershof.berlin/images/common/HSV_Logo.jpg",
    "telephone": "+49-162-2068729",
    "email": "hsv.berlin.adlershof@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Birkenweg",
      "addressLocality": "Berlin",
      "addressRegion": "BE",
      "postalCode": "12439",
      "addressCountry": "DE"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 52.445896,
      "longitude": 13.542682
    },
    "url": "https://www.hsv-adlershof.berlin",
    "priceRange": "€",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday"],
        "opens": "17:00",
        "closes": "19:00",
        "description": "Unterordnungstraining - Alle Leistungsstufen"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "09:30",
        "closes": "11:00",
        "description": "Gästetraining – aktuell nur Warteliste"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "10:00",
        "closes": "12:00",
        "description": "Unterordnungstraining - Anfänger & Fortgeschrittene"
      }
    ]
  };

  // Breadcrumb Structured Data
  const breadcrumbSchema = breadcrumbs ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url.startsWith('http') ? crumb.url : `${siteUrl}${crumb.url}`
    }))
  } : null;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:site_name" content="HSV Berlin-Adlershof e.V." />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={fullUrl} />
      <meta property="twitter:title" content={fullTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={fullImage} />
      
      {/* Canonical */}
      <link rel="canonical" href={fullUrl} />
      
      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="language" content="de" />
      <meta name="author" content="HSV Berlin-Adlershof e.V." />
      <meta name="geo.region" content="DE-BE" />
      <meta name="geo.placename" content="Berlin-Adlershof" />
      <meta name="geo.position" content="52.445896;13.542682" />
      <meta name="ICBM" content="52.445896, 13.542682" />

      {/* Structured Data - Organization */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Structured Data - Local Business */}
      <script type="application/ld+json">
        {JSON.stringify(localBusinessSchema)}
      </script>

      {/* Structured Data - Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Article Structured Data (for blog posts/news) */}
      {articleData && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": title,
            "description": description,
            "image": fullImage,
            "datePublished": articleData.publishedTime,
            "dateModified": articleData.modifiedTime || articleData.publishedTime,
            "author": {
              "@type": "Person",
              "name": articleData.author || "HSV Berlin-Adlershof e.V."
            },
            "publisher": {
              "@type": "Organization",
              "name": "HSV Berlin-Adlershof e.V.",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.hsv-adlershof.berlin/images/common/HSV_Logo.jpg"
              }
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;